import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listarPendientesCrm,
  obtenerIngestaMetricas,
  reintentarIngesta,
} from "@/lib/api";
import type { CrmPqrPendienteItem } from "@/lib/api";
import { formatearFechaSolo } from "@/lib/utils";
import { useDebounce } from "@/lib/useDebounce";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { HeroAnalista } from "@/components/HeroAnalista";
import { CardConexionMercurio } from "@/components/CardConexionMercurio";

const AREAS = [
  { value: "", label: "Todas" },
  { value: "BOLIVAR", label: "Bolívar" },
  { value: "CESAR", label: "Cesar" },
  { value: "CORDOBA", label: "Córdoba" },
  { value: "SUCRE", label: "Sucre" },
  { value: "MAGDALENA", label: "Magdalena" },
  { value: "ATLANTICO", label: "Atlántico" },
];

const LIMITES = [50, 100, 250, 500, 1000];

// Segmentos pill: filtros client-side por estado del caso.
type Segmento =
  | "todos"
  | "sin_procesar"
  | "pendiente_revision"
  | "pendiente_info"
  | "visita_tecnica"
  | "aprobado"
  | "fallo";

const SEGMENTOS: Array<{ value: Segmento; label: string }> = [
  { value: "todos", label: "Todos" },
  { value: "sin_procesar", label: "Sin procesar" },
  { value: "pendiente_revision", label: "Pendiente revisión" },
  { value: "pendiente_info", label: "Pendiente info" },
  { value: "visita_tecnica", label: "Visita técnica" },
  { value: "aprobado", label: "Cerrados" },
  { value: "fallo", label: "Fallaron" },
];

function pasaSegmento(it: CrmPqrPendienteItem, seg: Segmento): boolean {
  if (seg === "todos") return true;
  if (seg === "sin_procesar") return !it.expediente_id && !it.estado_cola;
  // Jerarquía excluyente //   visita_tecnica  > pendiente_info  > pendiente_revision
  // Visita técnica es el bloqueante más fuerte (sin la visita no hay
  // datos para la carta). Pendiente info engloba PENDIENTE_INFO del
  // motor + placeholders de carta sin aportar. Una vez resueltos
  // ambos, el caso "asciende" a Pendiente revisión.
  const requiereVisita = it.requiere_visita_tecnica === true;
  const tienePendientesCarta = (it.placeholders_pendientes_count ?? 0) > 0;
  const estaCerrado =
    it.estado_expediente === "APROBADO" ||
    it.estado_expediente === "RECHAZADO" ||
    it.estado_expediente === "CERRADO_APROBADO";

  if (seg === "visita_tecnica") return requiereVisita && !estaCerrado;
  if (seg === "pendiente_info") {
    if (requiereVisita || estaCerrado) return false;
    return it.estado_expediente === "PENDIENTE_INFO" || tienePendientesCarta;
  }
  if (seg === "pendiente_revision") {
    if (requiereVisita || tienePendientesCarta || estaCerrado) return false;
    return it.estado_expediente === "PENDIENTE_REVISION";
  }
  if (seg === "aprobado") return estaCerrado;
  if (seg === "fallo") return it.estado_cola === "FALLO";
  return true;
}

export function PendientesCrmPage() {
  const [params] = useSearchParams();
  // Deep-link: `/?segmento=pendiente_info` arranca con ese filtro.
  const segmentoInicial = (() => {
    const s = params.get("segmento");
    const validos: Segmento[] = [
      "todos",
      "sin_procesar",
      "pendiente_revision",
      "pendiente_info",
      "visita_tecnica",
      "aprobado",
      "fallo",
    ];
    return validos.includes(s as Segmento) ? (s as Segmento) : "todos";
  })();
  const [area, setArea] = useState<string>("");
  const [limite, setLimite] = useState<number>(100);
  const [segmento, setSegmento] = useState<Segmento>(segmentoInicial);
  const [nicInput, setNicInput] = useState("");
  const [radicadoInput, setRadicadoInput] = useState("");
  const [fechaDesde, setFechaDesde] = useState<string>("");
  const [fechaHasta, setFechaHasta] = useState<string>("");
  const nic = useDebounce(nicInput);
  const radicado = useDebounce(radicadoInput);

  const filtrosActivos =
    nic !== "" || radicado !== "" || fechaDesde !== "" || fechaHasta !== "";

  // Si el operador selecciona "Cerrados", traemos también los
  // expedientes APROBADO/RECHAZADO de los últimos 30 días que ya no
  // están activos en Mercurio.
  const incluirCerrados = segmento === "aprobado";

  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useQuery({
    queryKey: [
      "crm-pendientes",
      area,
      limite,
      nic,
      radicado,
      fechaDesde,
      fechaHasta,
      incluirCerrados,
    ],
    queryFn: () =>
      listarPendientesCrm({
        limite,
        area: area || undefined,
        nic: nic || undefined,
        radicado: radicado || undefined,
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
        incluir_cerrados: incluirCerrados,
      }),
    refetchInterval: 60_000,
  });

  const { puedeAdministrar } = useAuth();
  const esAdmin = puedeAdministrar();

  // Métricas del worker (polling cada 15 s).
  // solo el administrador necesita ver el detalle técnico del
  // worker; analista/supervisor reciben la info filtrada por la card de
  // conexión Mercurio o el banner de pendientes.
  const metricas = useQuery({
    queryKey: ["ingesta-metricas"],
    queryFn: obtenerIngestaMetricas,
    refetchInterval: 15_000,
    enabled: esAdmin,
  });

  function limpiarFiltros() {
    setNicInput("");
    setRadicadoInput("");
    setFechaDesde("");
    setFechaHasta("");
    setArea("");
  }

  const itemsCrudos = data?.items ?? [];
  const itemsFiltrados = itemsCrudos.filter((it) => pasaSegmento(it, segmento));

  function cuentaSeg(seg: Segmento) {
    return itemsCrudos.filter((it) => pasaSegmento(it, seg)).length;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="page page--wide">
        <div className="page-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              CRM Mercurio · GEMA
            </div>
            <h1>Bandeja</h1>
            <p className="sub">
              Todas las PQRs del piloto: las que entran del CRM, las que
              GEMA está procesando y las ya cerradas (últimos 30 días).
              Cada fila muestra el estado del motor.
            </p>
          </div>
          <div className="row row-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="btn btn--secondary"
              disabled={isFetching}
            >
              {isFetching ? "Actualizando…" : "Actualizar"}
            </button>
            <Link to="/expedientes/nuevo" className="btn btn--accent">
              📎 Subir radicado
            </Link>
          </div>
        </div>

        {/* Hero personalizado del analista (saludo + KPIs personales).
            Solo visible para roles humanos (analista/supervisor/admin) — el
            saludo no aplica al rol sistema. */}
        <HeroAnalista
          itemsBandeja={itemsCrudos}
        />

        {/* Banner del CRM con metadata del worker — solo admin. */}
        {esAdmin && <BannerWorker metricas={metricas.data} />}

        {/* Card de salud de conexión Mercurio CRM — solo admin. */}
        {esAdmin && (
          <CardConexionMercurio
            // Refleja la conexión REAL al CRM (query de pendientes), no la
            // métrica local del worker: última sync exitosa, si está
            // cargando, y si la última consulta a Mercurio falló.
            ultimaSync={data ? dataUpdatedAt : null}
            fetching={isFetching}
            error={!!error}
          />
        )}

        {/* Alerta PENDIENTE_INFO — solo admin (resto lo ve por filtro de la bandeja). */}
        {esAdmin && <BannerPendienteInfo metricas={metricas.data} />}

        {/* Filtros card */}
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card__head">
            <div className="icon">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 4h12l-4 5v4l-4-2v-2L2 4z" strokeLinejoin="round" />
              </svg>
            </div>
            <h3>Filtros</h3>
            <div className="meta">Combinan con AND. Vacíos = sin filtrar.</div>
            {filtrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="btn btn--ghost btn--sm row-end"
              >
                Limpiar
              </button>
            )}
          </div>
          <div className="card__body">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 14,
              }}
            >
              <div className="field">
                <label>NIC</label>
                <input
                  className="input"
                  placeholder="ej. 6600057"
                  value={nicInput}
                  onChange={(e) => setNicInput(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Radicado</label>
                <input
                  className="input mono"
                  placeholder="RE…"
                  value={radicadoInput}
                  onChange={(e) => setRadicadoInput(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Desde</label>
                <input
                  className="input"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Hasta</label>
                <input
                  className="input"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Departamento</label>
                <select
                  className="select"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                >
                  {AREAS.map((a) => (
                    <option key={a.value || "all"} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Límite</label>
                <select
                  className="select"
                  value={limite}
                  onChange={(e) => setLimite(Number(e.target.value))}
                >
                  {LIMITES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Segmentos pill */}
        <div className="row row-3" style={{ marginBottom: 14, flexWrap: "wrap" }}>
          <div className="segments">
            {SEGMENTOS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={
                  "segments__btn" + (segmento === s.value ? " is-active" : "")
                }
                onClick={() => setSegmento(s.value)}
              >
                {s.label}
                <span className="count">{cuentaSeg(s.value)}</span>
              </button>
            ))}
          </div>
          <span className="row-end muted" style={{ fontSize: 12.5 }}>
            <strong className="mono">{itemsFiltrados.length}</strong> resultados
            {filtrosActivos ? " (filtros aplicados)" : ""}
          </span>
        </div>

        {error && (
          <div className="banner banner--red">
            <span className="banner__icon">⚠</span>
            <div>
              No se pudo consultar el CRM (Mercurio):{" "}
              {(error as Error).message ?? "error al cargar pendientes"}. Revisa
              que la API esté arriba y que el conector CRM sea alcanzable desde
              este entorno.
            </div>
          </div>
        )}

        <Tabla
          items={itemsFiltrados}
          cargando={isLoading}
          total={data?.total ?? 0}
          limite={limite}
          filtrosActivos={filtrosActivos || segmento !== "todos"}
        />
      </main>
    </div>
  );
}

// ─── Banner del estado del worker ──────────────────────────────── //

function BannerWorker({
  metricas,
}: {
  metricas: import("@/lib/api").IngestaMetricas | undefined;
}) {
  if (!metricas) return null;
  if (!metricas.worker_activo) {
    return (
      <div className="banner banner--red" style={{ marginBottom: 18 }}>
        <span className="banner__icon">⚠</span>
        <div>
          <strong>Auto-ingesta inactiva</strong> (<span className="mono">GEMA_INGESTA_AUTO=0</span>).
          Los radicados deben ingerirse manualmente desde el detalle del CRM.
        </div>
      </div>
    );
  }
  const total = metricas.ok_hoy + metricas.fallo_hoy + metricas.omitido_hoy;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 18px",
        background:
          "linear-gradient(90deg, oklch(0.97 0.025 162), oklch(0.99 0.005 95) 70%)",
        border: "1px solid oklch(0.85 0.04 162)",
        borderRadius: "var(--r-lg)",
        marginBottom: 18,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "var(--emerald)",
          color: "white",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M3 5h12M3 9h12M3 13h12" strokeLinecap="round" />
          <circle cx="3" cy="5" r="1" fill="currentColor" />
          <circle cx="3" cy="9" r="1" fill="currentColor" />
          <circle cx="3" cy="13" r="1" fill="currentColor" />
        </svg>
      </div>
      <div>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Worker GEMA activo
        </h3>
        <p style={{ margin: "2px 0 0", color: "var(--ink-2)", fontSize: 13 }}>
          Polling cada <strong>{metricas.configuracion.intervalo_seg}s</strong>{" "}
          · bloques de <strong>{metricas.configuracion.batch_size}</strong> ·{" "}
          concurrencia <strong>{metricas.configuracion.concurrencia}</strong>
        </p>
      </div>
      <div
        className="row-end row row-3"
        style={{
          paddingLeft: 24,
          borderLeft: "1px solid oklch(0.85 0.04 162)",
        }}
      >
        <KpiPill label="Procesados hoy" value={total} tone="ink" />
        <KpiPill label="OK" value={metricas.ok_hoy} tone="green" />
        <KpiPill label="Falló" value={metricas.fallo_hoy} tone="red" />
        <KpiPill label="En cola" value={metricas.pendientes} tone="amber" />
        {metricas.procesando > 0 && (
          <KpiPill label="Proc." value={metricas.procesando} tone="blue" />
        )}
      </div>
    </div>
  );
}

function KpiPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "ink" | "green" | "red" | "amber" | "blue";
}) {
  const color = {
    ink: "var(--ink)",
    green: "var(--emerald-deep)",
    red: "var(--red-deep)",
    amber: "var(--amber-deep)",
    blue: "var(--blue-deep)",
  }[tone];
  return (
    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
      {label}
      <strong
        className="mono"
        style={{
          display: "block",
          color,
          fontSize: 14,
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function BannerPendienteInfo({
  metricas,
}: {
  metricas: import("@/lib/api").IngestaMetricas | undefined;
}) {
  if (!metricas || metricas.pendiente_info_hoy === 0) return null;
  return (
    <div className="banner" style={{ marginBottom: 18 }}>
      <span className="banner__icon">⚠</span>
      <div className="row row-2" style={{ width: "100%" }}>
        <span>
          <strong>{metricas.pendiente_info_hoy}</strong> caso(s) auto-ingestado(s)
          hoy quedaron en <strong>PENDIENTE_INFO</strong> — el motor necesita que
          el operador complete datos por chat antes de avanzar.
        </span>
        <Link
          to="/?segmento=pendiente_info"
          className="row-end"
          style={{ color: "var(--amber-deep)", textDecoration: "underline" }}
        >
          Ver en bandeja →
        </Link>
      </div>
    </div>
  );
}

// ─── Tabla unificada ────────────────────────────────────────────── //

function Tabla({
  items,
  cargando,
  total,
  limite,
  filtrosActivos,
}: {
  items: CrmPqrPendienteItem[];
  cargando: boolean;
  total: number;
  limite: number;
  filtrosActivos: boolean;
}) {
  const navigate = useNavigate();
  if (cargando) {
    return (
      <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
        Cargando pendientes desde el CRM…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="card empty" style={{ padding: 60 }}>
        <h3>{filtrosActivos ? "Ningún radicado coincide con los filtros." : "No hay PQRs pendientes en este corte."}</h3>
        <p className="muted">
          El worker chequea el CRM cada 60 s. Espera a que aparezcan o ajusta los filtros.
        </p>
      </div>
    );
  }
  const llegoTope = total >= limite;
  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Radicado</th>
            <th>Cliente / NIC</th>
            <th>Tipo PQR</th>
            <th title="Fecha en que el suscriptor radicó la PQR ante AFINIA.">
              Radicación
            </th>
            <th
              className="num"
              style={{ textAlign: "right" }}
              title="Días hábiles transcurridos desde la radicación (CCU/Ley 142: 15 hábiles para contestar)."
            >
              Días háb.
            </th>
            <th title="Fecha límite para contestar al usuario (CCU Afinia / Art 158 Ley 142).">
              Vence
            </th>
            <th
              className="num"
              style={{ textAlign: "right" }}
              title="Confianza heurística del motor sobre la decisión propuesta."
            >
              Conf.
            </th>
            <th>Estado GEMA</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const destino = it.expediente_id
              ? `/expedientes/${it.expediente_id}`
              : `/pqr/crm/${it.rad_open}`;
            const diasCls =
              (it.dias_habiles ?? 0) >= 13
                ? "var(--red-deep)"
                : (it.dias_habiles ?? 0) >= 10
                  ? "var(--amber-deep)"
                  : "var(--ink)";
            return (
              <tr
                key={it.rad_open}
                role="link"
                tabIndex={0}
                aria-label={`Abrir expediente ${it.rad_open}`}
                onClick={() => navigate(destino)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(destino);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <span className="mono" style={{ fontWeight: 500, fontSize: 12.5 }}>
                    {it.rad_open}
                  </span>
                  {it.requiere_clasificacion_gema && (
                    <span
                      className="badge badge--amber"
                      style={{ marginLeft: 6 }}
                      title="Sin tipología — GEMA clasificará al ingerir"
                    >
                      <span className="dot" />
                      Sin tipología
                    </span>
                  )}
                </td>
                <td>
                  <div>{it.cliente ?? <span className="muted">—</span>}</div>
                  <div className="mono muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {it.nic ?? "—"}
                  </div>
                </td>
                <td className="muted" style={{ maxWidth: 240 }}>
                  {it.tipo_pqr ?? <span className="muted">sin tipo</span>}
                </td>
                <td style={{ fontSize: 13, color: "var(--ink-2)" }}>
                  {it.fecha_radicacion ? (
                    formatearFechaSolo(it.fecha_radicacion)
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td
                  className="num"
                  style={{ textAlign: "right", color: diasCls, fontWeight: 500 }}
                >
                  {it.dias_habiles ?? <span className="muted">—</span>}
                </td>
                <td>
                  <ChipVencimiento
                    fecha={it.fecha_vencimiento ?? null}
                    diasHabiles={it.dias_habiles ?? null}
                  />
                </td>
                <td className="num" style={{ textAlign: "right" }}>
                  <ChipConfianza valor={it.confianza_motor ?? null} />
                </td>
                <td>
                  <span
                    className="row row-2"
                    style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}
                  >
                    <GatingBadge item={it} />
                    <EstadoGemaBadge item={it} />
                  </span>
                </td>
                <td>
                  <svg
                    className="row-arrow"
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M6 3l5 5-5 5" />
                  </svg>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {llegoTope && (
        <div
          className="row row-2"
          style={{
            padding: "10px 14px",
            background: "var(--amber-soft)",
            color: "var(--amber-deep)",
            borderTop: "1px solid oklch(0.85 0.10 80)",
            fontSize: 13,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="8" cy="8" r="6" />
            <path d="M8 5v3.5M8 11h.01" strokeLinecap="round" />
          </svg>
          <div>
            Se alcanzó el límite de <strong className="mono">{limite}</strong> resultados. Aplica filtros o
            sube el límite para ver más.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Badge unificado de estado por fila ────────────────────────── //

/** Badge de gating del auditor: aparece cuando el caso EXIGE revisión humana
 *  (el auditor LLM detectó una incoherencia carta↔decisión, una alerta omitida
 *  validada, o un riesgo jurídico con confianza). La severidad crítica va en
 *  rojo; alta/media en ámbar. Se pinta junto al estado, no lo reemplaza. */
function GatingBadge({ item }: { item: CrmPqrPendienteItem }) {
  if (item.gating_escalar !== true) return null;
  const sev = item.gating_severidad ?? "media";
  const clase = sev === "critica" ? "badge--red" : "badge--amber";
  const etiqueta =
    sev === "critica"
      ? "Revisar · crítico"
      : sev === "alta"
        ? "Revisar · alerta"
        : "Revisar";
  return (
    <span
      className={`badge ${clase}`}
      title="El auditor marcó este caso para revisión humana obligatoria."
    >
      <span className="dot" />⚠ {etiqueta}
    </span>
  );
}

function EstadoGemaBadge({ item }: { item: CrmPqrPendienteItem }) {
  const qc = useQueryClient();
  const reintentar = useMutation({
    mutationFn: () => reintentarIngesta(item.rad_open),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-pendientes"] });
      qc.invalidateQueries({ queryKey: ["ingesta-metricas"] });
    },
  });

  // 1) Cerrado por supervisor.
  if (item.estado_expediente === "APROBADO" || item.estado_expediente === "CERRADO_APROBADO") {
    return (
      <span className="badge badge--green">
        <span className="dot" />
        Aprobado
      </span>
    );
  }
  if (item.estado_expediente === "RECHAZADO") {
    return (
      <span className="badge badge--red">
        <span className="dot" />
        Rechazado
      </span>
    );
  }

  // 1.4) Visita técnica gana sobre todo lo demás cuando
  // el motor recomienda abrir orden de servicio. Sin la visita no hay
  // datos para llenar la carta, así que es el bloqueante real. El
  // badge ámbar va con sub-chip de la decisión preliminar y el tipo
  // de orden sugerido.
  if (item.requiere_visita_tecnica === true && item.expediente_id) {
    const tipo = item.tipo_orden_visita
      ? ` · ${item.tipo_orden_visita.replace(/_/g, " ")}`
      : "";
    return (
      <span className="row row-2" style={{ gap: 6, alignItems: "center" }}>
        <span
          className="badge badge--amber"
          title="El motor recomienda abrir orden de servicio antes de notificar."
        >
          <span className="dot" />
          Visita técnica{tipo}
        </span>
        {item.decision && (
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              padding: "2px 6px",
              border: "1px solid var(--border)",
              borderRadius: 4,
              whiteSpace: "nowrap",
            }}
            title="Decisión preliminar del motor (se confirma tras la visita)."
          >
            {item.decision.replace("_", " ")}
          </span>
        )}
      </span>
    );
  }

  // 1.5) Carta con placeholders sin aportar manda sobre la
  // decisión del motor: aunque haya IMPROCEDENTE/NO_ACCEDE/etc., el
  // operador no puede notificar al usuario hasta capturar los datos.
  // Se muestra como "Pendiente info" + sub-chip con la decisión.
  const pendientesCarta = item.placeholders_pendientes_count ?? 0;
  if (pendientesCarta > 0 && item.expediente_id) {
    return (
      <span className="row row-2" style={{ gap: 6, alignItems: "center" }}>
        <span
          className="badge badge--amber"
          title="La carta de notificación requiere capturar datos antes de poder enviarse."
        >
          <span className="dot" />
          Pendiente info · ⚠ {pendientesCarta} dato
          {pendientesCarta === 1 ? "" : "s"}
        </span>
        {item.decision && (
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              padding: "2px 6px",
              border: "1px solid var(--border)",
              borderRadius: 4,
              whiteSpace: "nowrap",
            }}
            title="Decisión preliminar del motor (no se notifica hasta completar los datos)."
          >
            {item.decision.replace("_", " ")}
          </span>
        )}
      </span>
    );
  }

  // 2) Procesado con decisión.
  if (item.estado_expediente === "PENDIENTE_REVISION" && item.decision) {
    const arts = item.articulos?.length ? ` · Art ${item.articulos.join("/")}` : "";
    const cls =
      item.decision === "ACCEDE" || item.decision === "ACCEDE_PARCIAL"
        ? "badge--green"
        : item.decision === "IMPROCEDENTE"
          ? "badge--violet"
          : "badge--red";
    return (
      <span className={`badge ${cls}`} title={`Decisión del motor${arts}`}>
        <span className="dot" />
        {item.decision.replace("_", " ")}
        <span className="muted" style={{ fontSize: 11 }}>{arts}</span>
      </span>
    );
  }

  // 3) Pendiente info / En ajuste.
  if (item.estado_expediente === "PENDIENTE_INFO") {
    return (
      <span
        className="badge badge--amber"
        title="GEMA procesó pero requiere que el operador complete datos por chat."
      >
        <span className="dot" />
        Pendiente info
      </span>
    );
  }
  if (item.estado_expediente === "EN_AJUSTE") {
    return (
      <span className="badge badge--amber">
        <span className="dot" />
        En ajuste
      </span>
    );
  }

  // 4) Cola (worker aún no terminó).
  if (item.estado_cola === "PROCESANDO") {
    return (
      <span className="badge badge--blue" style={{ animation: "pulse 1.6s infinite" }}>
        <span className="dot" />
        Procesando
      </span>
    );
  }
  if (item.estado_cola === "PENDIENTE") {
    return (
      <span className="badge badge--amber">
        <span className="dot" />
        En cola
      </span>
    );
  }
  if (item.estado_cola === "FALLO") {
    return (
      <span className="row row-2">
        <span
          className="badge badge--red"
          title={item.ultimo_error_cola ?? ""}
        >
          <span className="dot" />
          Falló ({item.intentos_cola ?? 0})
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            reintentar.mutate();
          }}
          disabled={reintentar.isPending}
          className="btn btn--ghost btn--sm"
        >
          {reintentar.isPending ? "…" : "Reintentar"}
        </button>
      </span>
    );
  }
  if (item.estado_cola === "OMITIDO") {
    return (
      <span className="badge" title="Ya existía expediente (ingesta manual previa).">
        <span className="dot" />
        Manual previo
      </span>
    );
  }

  // 5) Default.
  return (
    <span className="badge">
      <span className="dot" />
      Sin procesar
    </span>
  );
}

// ─── Chips de columna (UI-3) ──────────────────────────── //

/**
 * Chip de vencimiento. Muestra la fecha límite + días restantes con
 * semáforo. Negro = ok, ámbar = ≤ 3 días, rojo = vencido.
 *
 * Si el backend no envió `fecha_vencimiento`, derivamos una estimación
 * a partir de `dias_habiles` (CCU/Ley 142 = 15 hábiles desde la
 * radicación). Es una aproximación útil para que el supervisor vea
 * urgencia aunque Mercurio no lo reporte.
 */
function ChipVencimiento({
  fecha,
  diasHabiles,
}: {
  fecha: string | null;
  diasHabiles: number | null;
}) {
  let vencimiento: Date | null = null;
  if (fecha) {
    vencimiento = new Date(fecha);
  } else if (diasHabiles != null) {
    // 15 hábiles − días ya transcurridos = días restantes (aproximación
    // de calendario, suficiente para ranking visual).
    const restantesAprox = 15 - diasHabiles;
    vencimiento = new Date();
    vencimiento.setDate(vencimiento.getDate() + restantesAprox);
  }
  if (!vencimiento || isNaN(vencimiento.getTime())) {
    return <span className="muted">—</span>;
  }

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const v = new Date(vencimiento);
  v.setHours(0, 0, 0, 0);
  const diasRestantes = Math.round(
    (v.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24),
  );

  let color = "var(--ink)";
  let bg = "transparent";
  let etiqueta: string;
  if (diasRestantes < 0) {
    color = "white";
    bg = "var(--red-deep, oklch(0.42 0.20 25))";
    etiqueta = `Vencido (${Math.abs(diasRestantes)}d)`;
  } else if (diasRestantes === 0) {
    color = "white";
    bg = "var(--red, oklch(0.55 0.18 25))";
    etiqueta = "Hoy";
  } else if (diasRestantes <= 3) {
    color = "var(--amber-deep, oklch(0.45 0.16 75))";
    bg = "var(--amber-soft, oklch(0.95 0.05 80))";
    etiqueta = `${diasRestantes}d`;
  } else {
    color = "var(--ink-2)";
    bg = "transparent";
    etiqueta = `${diasRestantes}d`;
  }

  const fechaFmt = formatearFechaSolo(vencimiento.toISOString());
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 13, color: "var(--ink-2)" }}>{fechaFmt}</span>
      <span
        style={{
          alignSelf: "flex-start",
          fontSize: 11,
          fontWeight: 500,
          padding: "2px 8px",
          borderRadius: 999,
          color,
          background: bg,
          border: bg === "transparent" ? "1px solid var(--border)" : "none",
        }}
      >
        {etiqueta}
      </span>
    </div>
  );
}

/**
 * Chip de confianza del motor (0–1 → %). Semáforo:
 * ≥ 80 % verde · 70–80 % ámbar · < 70 % rojo · null = guion.
 */
function ChipConfianza({ valor }: { valor: number | null }) {
  if (valor == null) return <span className="muted">—</span>;
  const pct = Math.round(valor * 100);
  let color = "var(--emerald-deep, oklch(0.42 0.14 162))";
  let bg = "var(--emerald-soft, oklch(0.93 0.05 162))";
  if (pct < 70) {
    color = "var(--red-deep, oklch(0.42 0.20 25))";
    bg = "oklch(0.95 0.06 25)";
  } else if (pct < 80) {
    color = "var(--amber-deep, oklch(0.45 0.16 75))";
    bg = "var(--amber-soft, oklch(0.95 0.05 80))";
  }
  return (
    <span
      className="mono"
      style={{
        display: "inline-block",
        fontSize: 12,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 4,
        color,
        background: bg,
        minWidth: 44,
        textAlign: "center",
      }}
      title="Confianza heurística del motor (0–100 %)"
    >
      {pct}%
    </span>
  );
}
